import React, { useState, useEffect, useMemo } from "react";
import { Button, Grid, Tab, Typography } from "@mui/material";
import TabCardInterior from "../../../../components/Navigation/TabCardInterior/TabCardInterior";
import MarginalTaxBrackets from "@instream-solutions/instream-design-system/lib/Components/MarginalTaxBrackets/MarginalTaxBrackets";
import { MarginalTaxBracketsReducer } from "@instream-solutions/instream-design-system/lib/Utilities/reducerFunctions";
import {
  getCapitalGainsTaxes,
  saveCapitalGainsTaxes,
} from "../../../../api/advisor/taxes";
import { connect } from "react-redux";
import { fromJS } from "immutable";
import "../../../advisor/Settings/settings.scss";

const CapitalGainsTaxes = (props) => {
  const [state, setState] = useState({
    tabIndex: 0,
    isEditMode: false,
    isLoading: true,
  });

  const [singleTaxBracket, setSingleTaxBracket] = useState({
    data: [],
    backup: fromJS([]),
  });

  const [marriedSingleTaxBracket, setMarriedSingleTaxBracket] = useState({
    data: [],
    backup: fromJS([]),
  });

  const [marriedJointlyTaxBracket, setMarriedJointlyTaxBracket] = useState({
    data: [],
    backup: fromJS([]),
  });

  const [headOfHouseHoldTaxBracket, setHeadOfHouseHoldTaxBracket] = useState({
    data: [],
    backup: fromJS([]),
  });

  const currentUser = useMemo(() => props.currentUser.toJS());

  const scaleTaxRate = (scale) => (item) => {
    return {
      ...item,
      taxRate: item.taxRate * scale,
    };
  };

  useEffect(() => {
    const findTaxBracket = (taxBracket, data) => {
      return data.find((item) => {
        return item.taxFillingStatusType === taxBracket;
      });
    };

    getCapitalGainsTaxes(currentUser.id).then((response) => {
      const singleTaxBracket = findTaxBracket("SINGLE", response);
      setSingleTaxBracket((v) => {
        return {
          data: singleTaxBracket.marginalTaxBracketValues.map(
            scaleTaxRate(0.01)
          ),
          backup: fromJS(
            singleTaxBracket.marginalTaxBracketValues.map(scaleTaxRate(0.01))
          ),
        };
      });

      const marriedFilingJointly = findTaxBracket(
        "MARRIED_FILING_JOINTLY",
        response
      );
      setMarriedJointlyTaxBracket((v) => {
        return {
          data: marriedFilingJointly.marginalTaxBracketValues.map(
            scaleTaxRate(0.01)
          ),
          backup: fromJS(
            marriedFilingJointly.marginalTaxBracketValues.map(
              scaleTaxRate(0.01)
            )
          ),
        };
      });

      const marriedFilingSeparately = findTaxBracket(
        "MARRIED_FILING_SEPARATELY",
        response
      );
      setMarriedSingleTaxBracket((v) => {
        return {
          data: marriedFilingSeparately.marginalTaxBracketValues.map(
            scaleTaxRate(0.01)
          ),
          backup: fromJS(
            marriedFilingSeparately.marginalTaxBracketValues.map(
              scaleTaxRate(0.01)
            )
          ),
        };
      });

      const headOfHousehold = findTaxBracket("HEAD_OF_HOUSEHOLD", response);
      setHeadOfHouseHoldTaxBracket({
        data: headOfHousehold.marginalTaxBracketValues.map(scaleTaxRate(0.01)),
        backup: fromJS(
          headOfHousehold.marginalTaxBracketValues.map(scaleTaxRate(0.01))
        ),
      });

      setState({
        ...state,
        isLoading: false,
      });
    });
  }, []);

  const singleTaxBracketReducer = MarginalTaxBracketsReducer(
    "data",
    "inflationRate",
    singleTaxBracket,
    setSingleTaxBracket
  );

  const marriedSingleTaxBracketReducer = MarginalTaxBracketsReducer(
    "data",
    "inflationRate",
    marriedSingleTaxBracket,
    setMarriedSingleTaxBracket
  );

  const marriedJointlyTaxBracketReducer = MarginalTaxBracketsReducer(
    "data",
    "inflationRate",
    marriedJointlyTaxBracket,
    setMarriedJointlyTaxBracket
  );

  const headOfHouseHoldTaxBracketReducer = MarginalTaxBracketsReducer(
    "data",
    "inflationRate",
    headOfHouseHoldTaxBracket,
    setHeadOfHouseHoldTaxBracket
  );

  const changePanel = (event, newValue) => {
    setState({
      ...state,
      tabIndex: newValue,
    });
  };

  const setEditMode = (updateFn, isEditMode) => {
    updateFn((v) => {
      return {
        ...v,
        data: v.data.map((item, index) => {
          item.inEdit = isEditMode;
          item.editIndex = index;
          return item;
        }),
      };
    });
  };

  const cancelTaxBracket = (updateFn) => {
    updateFn((v) => {
      return {
        ...v,
        data: v.backup.toJS(),
      };
    });
  };

  const cancelMarginalTaxBrackets = () => {
    setState({
      ...state,
      isEditMode: false,
    });

    cancelTaxBracket(setSingleTaxBracket);
    cancelTaxBracket(setMarriedSingleTaxBracket);
    cancelTaxBracket(setMarriedJointlyTaxBracket);
    cancelTaxBracket(setHeadOfHouseHoldTaxBracket);
  };

  const updateBackup = (updateFn) => {
    updateFn((v) => {
      return {
        ...v,
        data: v.data.map((item) => {
          return {
            ...item,
            inEdit: false,
          };
        }),
        backup: fromJS(
          v.data.map((item) => {
            return {
              ...item,
              inEdit: false,
            };
          })
        ),
      };
    });
  };

  const saveMarginalTaxBrackets = () => {
    setState({
      ...state,
      isEditMode: false,
    });

    const payload = [
      {
        scope: "Firm",
        stateId: null,
        stateName: null,
        taxFillingStatusType: "SINGLE",
        inflationRate: 1.8,
        marginalTaxBracketValues: singleTaxBracket.data.map(scaleTaxRate(100)),
        isDefault: false,
        taxBracketType: "CAPITAL_GAINS",
      },
      {
        scope: "Firm",
        stateId: null,
        stateName: null,
        taxFillingStatusType: "MARRIED_FILING_JOINTLY",
        inflationRate: 1.8,
        marginalTaxBracketValues: marriedJointlyTaxBracket.data.map(
          scaleTaxRate(100)
        ),
        isDefault: false,
        taxBracketType: "CAPITAL_GAINS",
      },
      {
        scope: "Firm",
        stateId: null,
        stateName: null,
        taxFillingStatusType: "MARRIED_FILING_SEPARATELY",
        inflationRate: 1.8,
        marginalTaxBracketValues: marriedSingleTaxBracket.data.map(
          scaleTaxRate(100)
        ),
        isDefault: false,
        taxBracketType: "CAPITAL_GAINS",
      },
      {
        scope: "Firm",
        stateId: null,
        stateName: null,
        taxFillingStatusType: "HEAD_OF_HOUSEHOLD",
        inflationRate: 1.8,
        marginalTaxBracketValues: headOfHouseHoldTaxBracket.data.map(
          scaleTaxRate(100)
        ),
        isDefault: false,
        taxBracketType: "CAPITAL_GAINS",
      },
    ];

    saveCapitalGainsTaxes(currentUser.id, payload).then(() => {
      updateBackup(setSingleTaxBracket);
      updateBackup(setMarriedSingleTaxBracket);
      updateBackup(setMarriedJointlyTaxBracket);
      updateBackup(setHeadOfHouseHoldTaxBracket);
    });
  };

  return (
    <Grid
      className={"advisor-settings-page settings-page"}
      container
      spacing={3}
    >
      <Grid item xs={7}>
        <Typography component={"h1"} variant={"h1"}>
          Capital Gains Tax Bracket
        </Typography>
      </Grid>
      <Grid item xs={5}>
        &nbsp;
      </Grid>
      <Grid item xs={12}>
        <Typography component={"p"} variant={"p"}>
          Long-term capital gains tax is a tax applied to assets held for more
          than a year. The long-term capital gains tax rates are typically much
          lower than the Federal/State Marginal Tax Bracket.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TabCardInterior value={state.tabIndex} onChange={changePanel}>
          <Tab label={"Single"} id={"state-tax-tab-1"} className="tab-header" />
          <Tab
            label={"Married Filing Separately"}
            id={"state-tax-tab-2"}
            className="tab-header"
          />
          <Tab
            label={"Head of Household"}
            id={"state-tax-tab-3"}
            className="tab-header"
          />
          <Tab
            label={"Married Filing Joint"}
            id={"state-tax-tab-4"}
            className="tab-header"
          />
        </TabCardInterior>
      </Grid>
      <Grid item xs={12}>
        {state.tabIndex === 0 && (
          <MarginalTaxBrackets
            inflationRate={0}
            data={singleTaxBracket.data}
            reducer={singleTaxBracketReducer}
            inEdit={true}
            isStatic={true}
          />
        )}
        {state.tabIndex === 1 && (
          <MarginalTaxBrackets
            inflationRate={0}
            data={marriedSingleTaxBracket.data}
            reducer={marriedSingleTaxBracketReducer}
            inEdit={true}
            isStatic={true}
          />
        )}
        {state.tabIndex === 2 && (
          <MarginalTaxBrackets
            inflationRate={0}
            data={headOfHouseHoldTaxBracket.data}
            reducer={headOfHouseHoldTaxBracketReducer}
            inEdit={true}
            isStatic={true}
          />
        )}
        {state.tabIndex === 3 && (
          <MarginalTaxBrackets
            inflationRate={0}
            data={marriedJointlyTaxBracket.data}
            reducer={marriedJointlyTaxBracketReducer}
            inEdit={true}
            isStatic={true}
          />
        )}
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    currentUser: state.currentUserReducer,
  };
};

export default connect(mapStateToProps)(CapitalGainsTaxes);
